import type { Checklist } from '@/services/checklists';
import type { ItineraryItem } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import type { TripMemoryPreview } from '@/services/tripMemories';
import { buildTripRecapMetrics, type TripRecapMetrics } from '@/services/dossier';
import type { TripBudgetSnapshot } from '@/types/budget';
import { toLocalDateKey } from '@/utils/dateKeys';

export type TripSummaryPlace = {
  id: string;
  title: string;
  subtitle?: string;
  dateKey: string;
  coordinates?: { latitude: number; longitude: number } | null;
};

export type TripSummaryDay = {
  dateKey: string;
  title: string;
  highlights: ItineraryItem[];
};

export type TripSummaryMoment = {
  id: string;
  title: string;
  dateLabel: string;
  category: ItineraryItem['category'];
  location?: string;
};

export type TripSummary = {
  tripId: string;
  metrics: TripRecapMetrics;
  coverImage?: string | null;
  days: TripSummaryDay[];
  topMoments: TripSummaryMoment[];
  places: TripSummaryPlace[];
  mappedPlaces: TripSummaryPlace[];
  unmappedPlaces: TripSummaryPlace[];
  checklist: {
    completed: number;
    total: number;
  };
  collaboratorCount: number;
  memoryPreview?: TripMemoryPreview | null;
};

type BuildTripSummaryOptions = {
  itineraryItems?: ItineraryItem[];
  checklist?: Checklist | null;
  budgetSnapshot?: TripBudgetSnapshot;
  memoryPreview?: TripMemoryPreview | null;
  coverImage?: string | null;
};

const itemHasPlace = (item: ItineraryItem): boolean => {
  return Boolean((item.location || item.address || '').trim());
};

const GENERIC_MOMENT_PATTERNS = [
  /^return to hotel/i,
  /^hotel check-?in/i,
  /^check-?in\b/i,
  /^rest\b/i,
  /^buffer\b/i,
  /^nap\b/i,
  /^meal\b/i,
  /^restroom\b/i,
  /^test$/i,
];

const isGenericMoment = (title: string): boolean =>
  GENERIC_MOMENT_PATTERNS.some((pattern) => pattern.test(title.trim()));

const scoreMoment = (item: ItineraryItem): number => {
  let score = 0;
  if (item.coordinates) score += 4;
  if ((item.location || item.address || '').trim()) score += 3;
  if (item.category === 'activity') score += 2;
  if (item.category === 'flight' || item.category === 'ferry' || item.category === 'train') score += 1;
  if (item.category === 'hotel') score -= 1;
  if (isGenericMoment(item.title)) score -= 6;
  return score;
};

export const pickTopMoments = (itineraryItems: ItineraryItem[], limit = 5): TripSummaryMoment[] => {
  const ranked = [...itineraryItems]
    .filter((item) => !isGenericMoment(item.title))
    .sort((a, b) => scoreMoment(b) - scoreMoment(a));

  const picked: TripSummaryMoment[] = [];
  const usedDays = new Set<string>();

  for (const item of ranked) {
    if (picked.length >= limit) break;
    const dateKey = toLocalDateKey(item.startDate);
    if (usedDays.has(dateKey) && picked.length >= 2) continue;

    picked.push({
      id: item.id,
      title: item.title,
      dateLabel: new Date(`${dateKey}T12:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      category: item.category,
      location: item.location || item.address || undefined,
    });
    usedDays.add(dateKey);
  }

  return picked;
};

export const buildTripSummary = (trip: Trip, options: BuildTripSummaryOptions = {}): TripSummary => {
  const itineraryItems = [...(options.itineraryItems ?? [])].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  const places: TripSummaryPlace[] = itineraryItems
    .filter(itemHasPlace)
    .map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.location || item.address,
      dateKey: toLocalDateKey(item.startDate),
      coordinates: item.coordinates ?? null,
    }));
  const mappedPlaces = places.filter((place) => !!place.coordinates);
  const unmappedPlaces = places.filter((place) => !place.coordinates);

  const groupedDays = new Map<string, ItineraryItem[]>();
  itineraryItems.forEach((item) => {
    const key = toLocalDateKey(item.startDate);
    groupedDays.set(key, [...(groupedDays.get(key) ?? []), item]);
  });

  const days: TripSummaryDay[] = Array.from(groupedDays.entries()).map(([dateKey, items]) => ({
    dateKey,
    title: new Date(`${dateKey}T12:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    highlights: items.slice(0, 3),
  }));

  const checklistItems = options.checklist?.items ?? [];
  const completedChecklistItems = checklistItems.filter((item) => item.completed).length;
  const metrics = buildTripRecapMetrics(trip, options.budgetSnapshot, {
    memoryCount: options.memoryPreview?.count ?? 0,
    memorySnippet: options.memoryPreview?.latestSnippet,
    placesVisitedCount: mappedPlaces.length || places.length || (trip.destinations?.length ?? (trip.destination ? 1 : 0)) || 1,
  });

  return {
    tripId: trip.id,
    metrics,
    coverImage: options.coverImage ?? trip.landmarkImage ?? null,
    days,
    topMoments: pickTopMoments(itineraryItems),
    places,
    mappedPlaces,
    unmappedPlaces,
    checklist: {
      completed: completedChecklistItems,
      total: checklistItems.length,
    },
    collaboratorCount: trip.collaborators?.length ?? 0,
    memoryPreview: options.memoryPreview ?? null,
  };
};
